import type { FundingNeedDraft } from '../models/FundingNeeds'

const serverURL = import.meta.env.VITE_API_URL

export interface SuggestionItem {
  title: string
}

export interface SuggestionsJobStatus {
  status: 'running' | 'completed' | 'error'
  progress: number
  suggestions: SuggestionItem[]
  session_id?: number
  error?: string
}

export interface StartSuggestionsParams {
  org_uuid: string
  include_gambling: boolean
  include_international: boolean
}

export interface LatestSession {
  id: number
  suggestions: SuggestionItem[]
  include_gambling: boolean
  include_international: boolean
}

export class UsageLimitError extends Error {
  type: string
  days_remaining?: number
  latest_session?: LatestSession

  constructor(detail: {
    type: string
    days_remaining?: number
    message: string
    latest_session?: LatestSession
  }) {
    super(detail.message)
    this.type = detail.type
    this.days_remaining = detail.days_remaining
    this.latest_session = detail.latest_session ?? undefined
  }
}

export async function startSuggestionsJob(
  token: string,
  params: StartSuggestionsParams,
): Promise<{ job_id: string }> {
  const response = await fetch(
    `${serverURL}/funding-needs/funding-ideas/suggestions`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    },
  )

  if (response.status === 402) {
    const error = await response.json()
    throw new UsageLimitError(error.detail)
  }

  if (!response.ok) {
    throw new Error('Failed to start suggestions job')
  }

  return response.json()
}

export async function pollSuggestionsJob(
  token: string,
  jobId: string,
): Promise<SuggestionsJobStatus> {
  const response = await fetch(
    `${serverURL}/funding-needs/funding-ideas/suggestions/${jobId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  )

  if (!response.ok) {
    throw new Error('Failed to poll suggestions job')
  }

  return response.json()
}

export interface GenerateDraftParams {
  org_uuid: string
  session_id: number
  suggestion_title: string
  interest_reason: string
}

export async function checkFundingIdeasUsage(
  token: string,
  orgUuid: string,
): Promise<void> {
  const response = await fetch(
    `${serverURL}/funding-needs/funding-ideas/usage?org_uuid=${orgUuid}`,
    { headers: { Authorization: `Bearer ${token}` } },
  )
  if (!response.ok) {
    throw new Error('Failed to check usage')
  }
  const data = await response.json()
  if (!data.can_use) {
    throw new UsageLimitError({
      type: data.type,
      days_remaining: data.days_remaining,
      message: data.type,
      latest_session: data.latest_session ?? undefined,
    })
  }
}

export async function cancelFundingIdeasJob(
  token: string,
  jobId: string,
): Promise<void> {
  const response = await fetch(
    `${serverURL}/funding-needs/funding-ideas/suggestions/${jobId}`,
    { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
  )
  if (!response.ok && response.status !== 404) {
    throw new Error('Failed to cancel scan. Please try again.')
  }
  // 404 = already gone, treat as success
}

export async function generateDraftRequest(
  token: string,
  params: GenerateDraftParams,
): Promise<FundingNeedDraft> {
  const response = await fetch(
    `${serverURL}/funding-needs/funding-ideas/draft`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    },
  )

  if (!response.ok) {
    throw new Error('Failed to generate draft')
  }

  return response.json()
}
